'use client';

import { Description, Field } from '@headlessui/react';
import React, { useCallback, useEffect, useState } from 'react';

import { Label, type TextFieldProps, TextFiled } from '@/shared';

type AllowedInputTypes = 'password' | 'text' | 'number' | 'tel';

type OptInProps = TextFieldProps & {
	name: string;
	action?: React.ReactNode;
	length?: number;
	inputType?: AllowedInputTypes;
	defaultValue?: string;
};

export function OptIn({
	name,
	label,
	action,
	hint,
	size = 'md',
	length = 6,
	inputType = 'number',
	defaultValue = '',
	...props
}: OptInProps) {
	const [activeInput, setActiveInput] = React.useState(0);
	const inputRefs = React.useRef<Array<HTMLInputElement | null>>([]);

	const [value, setValue] = useState<string[]>(() => defaultValue.split(''));
	const setValueByIndex = useCallback((index: number, val: string) => {
		setValue((prevValue) => {
			const newValue = [...prevValue];
			newValue[index] = val;
			return newValue;
		});
	}, []);

	const isInputNum = inputType === 'number' || inputType === 'tel';

	useEffect(() => {
		inputRefs.current = inputRefs.current.slice(0, length);
	}, [length]);

	const onChange = (
		event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
	) => {
		const inputValue = event.target.value;

		// Обработка ввода одного символа
		if (inputValue.length === 1) {
			setValueByIndex(activeInput, inputValue);
			focusInput(activeInput + 1);
		}
		// Обработка множественных символов (из вставки или других методов ввода)
		else if (inputValue.length > 1) {
			const characters = inputValue.split('').slice(0, length);

			// Валидация символов для числовых полей
			if (isInputNum && characters.some((char) => isNaN(Number(char)))) {
				return;
			}

			// Установка всех символов сразу
			setValue((val) => {
				const newValue = [...val];
				for (const [index, char] of characters.entries()) {
					if (index < length) {
						newValue[index] = char;
					}
				}
				return newValue;
			});

			// Фокус на следующее пустое поле или последнее заполненное
			const nextFocusIndex = Math.min(characters.length, length - 1);
			focusInput(nextFocusIndex);
		}
	};

	const onFocus = useCallback(
		(index: number) =>
			(event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
				setActiveInput(index);
				event.target.select();
			},
		[setActiveInput],
	);

	const onBlur = () => {
		setActiveInput(activeInput - 1);
	};

	const onKeyDown = (
		event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
	) => {
		// Разрешить операции вставки (Ctrl+V, Cmd+V)
		if ((event.ctrlKey || event.metaKey) && event.key === 'v') {
			return;
		}

		if ([event.code, event.key].includes('Backspace')) {
			event.preventDefault();
			changeCodeAtFocus('');
			focusInput(activeInput - 1);
		} else if (event.code === 'Delete') {
			event.preventDefault();
			changeCodeAtFocus('');
		} else if (event.code === 'ArrowLeft') {
			event.preventDefault();
			focusInput(activeInput - 1);
		} else if (event.code === 'ArrowRight') {
			event.preventDefault();
			focusInput(activeInput + 1);
		}
		// React не вызывает onChange при повторном вводе того же значения
		// Поэтому нужно вручную сфокусироваться на следующем поле
		else if (event.key === value[activeInput]) {
			event.preventDefault();
			focusInput(activeInput + 1);
		} else if (
			event.code === 'Spacebar' ||
			event.code === 'Space' ||
			event.code === 'ArrowUp' ||
			event.code === 'ArrowDown'
		) {
			event.preventDefault();
		} else if (isInputNum && !(event.key >= '0' && event.key <= '9')) {
			event.preventDefault();
		} else if (!isInputNum && event.key.length > 1) {
			// Блокировать специальные клавиши для текстовых полей
			event.preventDefault();
		} else {
			changeCodeAtFocus('');
		}
	};

	const focusInput = (index: number) => {
		const activeInput = Math.max(Math.min(length - 1, index), 0);

		if (inputRefs.current[activeInput]) {
			inputRefs.current[activeInput]?.focus();
			inputRefs.current[activeInput]?.select();
			setActiveInput(activeInput);
		}
	};

	const changeCodeAtFocus = (val: string) => {
		setValueByIndex(activeInput, val[0]);
	};

	const handlePaste = (
		event: React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>,
	) => {
		event.preventDefault();

		const pastedData = event.clipboardData
			.getData('text/plain')
			.replace(/\s/g, '') // Удалить пробелы
			.slice(0, length)
			.split('');

		// Предотвратить вставку, если данные буфера содержат нечисловые значения для числовых полей
		if (isInputNum && pastedData.some((value) => isNaN(Number(value)))) {
			return;
		}

		// Очистить текущие значения и вставить новые данные
		setValue(() => {
			const newValue = new Array(length).fill('');
			for (const [index, value] of pastedData.entries()) {
				if (index < length) {
					newValue[index] = value;
				}
			}
			return newValue;
		});

		// Фокус на следующее пустое поле или последнее заполненное
		const nextFocusIndex = Math.min(pastedData.length, length - 1);
		focusInput(nextFocusIndex);
	};

	return (
		<Field>
			<input type="hidden" name={name} value={value.join('')} />
			{label && (
				<div className="mb-2">
					<Label action={action}>{label}</Label>
				</div>
			)}
			<div className="flex flex-row justify-between gap-2">
				{Array.from({ length: length }, (_, index) => index).map((index) => (
					<React.Fragment key={index}>
						<TextFiled
							ref={(el: any) => (inputRefs.current[index] = el)}
							value={value[index] ?? ''}
							onChange={onChange}
							onFocus={onFocus(index)}
							onBlur={onBlur}
							onKeyDown={onKeyDown}
							onPaste={handlePaste}
							size={size}
							type={isInputNum ? 'text' : inputType}
							inputMode={isInputNum ? 'numeric' : undefined}
							className={'text-center'}
							invalid={props?.invalid}
						/>
					</React.Fragment>
				))}
			</div>
			{hint && (
				<Description className="mt-1 block text-sm/6 text-gray-600">
					{hint}
				</Description>
			)}
		</Field>
	);
}
